import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { InstructorWithProfile, SpecialtyType, ProfileRow } from '@/types/database'
import { PLATFORM_FEE_PERCENT } from '@/lib/constants'

// Shape composto do select com joins opcionais que esse hook usa. Centraliza
// os campos extra e elimina `as any` no consumo.
interface InstructorWithExtras extends InstructorWithProfile {
  specialties?: { specialty: SpecialtyType }[] | null
  availability?: { day_of_week: number; start_time: string; end_time: string }[] | null
  reviews?: Array<{
    id: string
    rating: number
    comment: string | null
    instructor_reply: string | null
    instructor_reply_at: string | null
    created_at: string
    student: Pick<ProfileRow, 'full_name' | 'avatar_url'> | null
  }> | null
}

// ─── Filter shape ─────────────────────────────────────────────────────────────

type SortOption = 'rating_desc' | 'price_asc' | 'price_desc' | 'experience_desc'

interface InstructorFilters {
  /** Cidade escolhida no seletor (nome do IBGE). Comparação exata, sem acento/caixa. */
  city?: string
  /** UF (ex.: 'SP'). */
  state?: string
  /** "Procurar nas proximidades": centro da cidade e raio em km. */
  near?: { lat: number; lng: number; km: number }
  specialties?: string[]
  minRating?: number
  minPrice?: number
  maxPrice?: number
  search?: string
  sort?: SortOption
  availableToday?: boolean
  availableThisWeek?: boolean
  availableDate?: string // "YYYY-MM-DD"
  womenOnly?: boolean
  // I1 — filtros avançados
  query?: string          // alias para search (busca por nome)
  onlyFemale?: boolean    // alias para womenOnly
  /** @deprecated Instrutor Pro foi descontinuado; ignorado. */
  onlyPro?: boolean
  availability?: {        // dia_da_semana + período
    dayOfWeek: number
    period: 'morning' | 'afternoon' | 'evening'
  }
  vehicle?: string        // tipo de veículo (ex: 'manual', 'automatico')
  // P1 — filtros avançados
  minExperienceYears?: number
}

// ─── Query key factory ────────────────────────────────────────────────────────

const instructorKeys = {
  all: ['instructors'] as const,
  list: (filters?: InstructorFilters) =>
    [...instructorKeys.all, 'list', filters ?? {}] as const,
  detail: (slug: string) => [...instructorKeys.all, 'detail', slug] as const,
}

// ─── Fetchers ─────────────────────────────────────────────────────────────────

/** "São Paulo" e "sao paulo" comparam iguais. */
function normalizePlace(v: string): string {
  return v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()
}

/** Distância em km entre dois pontos (haversine). */
function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = (d: number) => (d * Math.PI) / 180
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2
    + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2
  return 6371 * 2 * Math.asin(Math.sqrt(a))
}

// Mapa de período para intervalos de horas
const PERIOD_HOURS: Record<'morning' | 'afternoon' | 'evening', [number, number]> = {
  morning:   [6,  12],
  afternoon: [12, 18],
  evening:   [18, 23],
}

// Colunas públicas da tabela instructors — exclui dados sensíveis:
// cnh_number, vehicle_plate, mp_user_id, identity_doc_url,
// cnh_review_note, rejection_reason, reviewed_by, identity_doc_note.
// Mantém apenas o que é necessário para vitrine e perfil público.
const PUBLIC_INSTRUCTOR_FIELDS = [
  'id', 'profile_id', 'bio', 'years_experience',
  'cnh_verified', 'cnh_status',
  'vehicle_model', 'vehicle_year', 'vehicle_photos',
  'hourly_rate', 'service_radius_km',
  'latitude', 'longitude',
  'status', 'rating_avg', 'total_lessons',
  'slug', 'created_at',
  'whatsapp', 'instagram_url', 'linkedin_url',
  'is_woman_instructor',
  'identity_doc_status',
  'teaching_since', 'specializations', 'languages',
  'video_intro_url', 'certifications', 'highlights',
  'vacation_mode', 'vacation_start', 'vacation_end',
  'is_featured',
].join(', ')

async function fetchInstructors(
  filters?: InstructorFilters,
): Promise<InstructorWithExtras[]> {
  // Normaliza aliases
  const normalizedFilters: InstructorFilters | undefined = filters
    ? {
        ...filters,
        search:    filters.query ?? filters.search,
        womenOnly: filters.onlyFemale ?? filters.womenOnly,
      }
    : undefined

  const f = normalizedFilters

  const needsAvailability = f?.availableToday || f?.availableThisWeek || f?.availableDate || f?.availability

  // Campos públicos do perfil — exclui phone para não vazar PII na listagem
  const publicProfileFields = 'id, full_name, avatar_url, role, city, state, is_pro_instructor, created_at'

  let query = supabase
    .from('instructors')
    .select(
      needsAvailability
        ? `${PUBLIC_INSTRUCTOR_FIELDS}, profile:profiles!instructors_profile_id_fkey(${publicProfileFields}), specialties:instructor_specialties(specialty), availability:availability(day_of_week, start_time, end_time)`
        : `${PUBLIC_INSTRUCTOR_FIELDS}, profile:profiles!instructors_profile_id_fkey(${publicProfileFields}), specialties:instructor_specialties(specialty)`,
    )
    .eq('status', 'approved')

  if (f?.minRating !== undefined) {
    query = query.gte('rating_avg', f.minRating)
  }
  // O aluno filtra pelo preço que ELE paga; no banco está o valor do
  // instrutor (preço ao aluno = valor × (1 + PLATFORM_FEE_PERCENT/100)).
  const toInstructor = (v: number) => v / (1 + PLATFORM_FEE_PERCENT / 100)
  if (f?.minPrice !== undefined) {
    query = query.gte('hourly_rate', toInstructor(f.minPrice))
  }
  if (f?.maxPrice !== undefined) {
    query = query.lte('hourly_rate', toInstructor(f.maxPrice))
  }
  if (f?.womenOnly) {
    query = query.eq('is_woman_instructor', true)
  }

  const sort = f?.sort ?? 'rating_desc'
  if (sort === 'price_asc') {
    query = query.order('hourly_rate', { ascending: true })
  } else if (sort === 'price_desc') {
    query = query.order('hourly_rate', { ascending: false })
  } else if (sort === 'experience_desc') {
    query = query.order('total_lessons', { ascending: false })
  } else {
    query = query.order('rating_avg', { ascending: false })
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)

  let results = (data ?? []) as unknown as InstructorWithExtras[]

  // Não há mais plano pago de instrutor (decisão de 2026-10-11): ninguém
  // "fura a fila" da busca; a ordem é a pedida pelo aluno.

  // Client-side filters
  if (f?.search) {
    const term = f.search.toLowerCase()
    results = results.filter((inst) => {
      const name = inst.profile?.full_name ?? ''
      const bio  = inst.bio ?? ''
      return name.toLowerCase().includes(term) || bio.toLowerCase().includes(term)
    })
  }

  // Localização: UF + cidade exatas; com "proximidades", entra também quem
  // está a até `near.km` do centro da cidade (coordenadas do instrutor).
  if (f?.state || f?.city || f?.near) {
    const uf = f.state ? normalizePlace(f.state) : null
    const city = f.city ? normalizePlace(f.city) : null
    results = results.filter((inst) => {
      const sameState = !uf || normalizePlace(inst.profile?.state ?? '') === uf
      const sameCity = !city || normalizePlace(inst.profile?.city ?? '') === city
      if (sameState && sameCity) return true
      if (f.near && inst.latitude != null && inst.longitude != null) {
        return distanceKm(f.near.lat, f.near.lng, Number(inst.latitude), Number(inst.longitude)) <= f.near.km
      }
      return false
    })
  }

  if (f?.specialties && f.specialties.length > 0) {
    results = results.filter((inst) => {
      const raw = inst.specialties ?? []
      const instSpecialties = raw.map((s) => s.specialty)
      return f.specialties!.some((sp) => instSpecialties.includes(sp as SpecialtyType))
    })
  }

  // I1: filtro de veículo (client-side via vehicle_model)
  if (f?.vehicle) {
    const vehicleTerm = f.vehicle.toLowerCase()
    results = results.filter((inst) =>
      (inst.vehicle_model ?? '').toLowerCase().includes(vehicleTerm),
    )
  }

  // P1: filtro de anos de experiência mínimos
  if (f?.minExperienceYears !== undefined && f.minExperienceYears > 0) {
    results = results.filter((inst) =>
      (inst.years_experience ?? 0) >= f.minExperienceYears!,
    )
  }

  // ── Availability filtering with exception awareness ──────────────────────────

  async function applyAvailabilityFilter(
    list: InstructorWithExtras[],
    targetDow: number,
    targetDate: string | null,
  ): Promise<InstructorWithExtras[]> {
    const allIds = list.map((i) => i.id)
    const exceptionMap: Record<string, boolean> = {}

    if (targetDate && allIds.length > 0) {
      const { data: exceptions } = await supabase
        .from('availability_exceptions')
        .select('instructor_id, is_available')
        .eq('date', targetDate)
        .in('instructor_id', allIds)

      for (const ex of exceptions ?? []) {
        exceptionMap[ex.instructor_id] = ex.is_available
      }
    }

    return list.filter((inst) => {
      if (exceptionMap[inst.id] !== undefined) return exceptionMap[inst.id]
      const av = inst.availability ?? []
      return av.some((a) => a.day_of_week === targetDow)
    })
  }

  if (f?.availableToday) {
    const today = new Date()
    results = await applyAvailabilityFilter(results, today.getDay(), today.toISOString().slice(0, 10))
  }

  if (f?.availableThisWeek && !f?.availableToday) {
    const todayDow = new Date().getDay()
    const remainingDays = Array.from({ length: 7 }, (_, i) => (todayDow + i) % 7)
    results = results.filter((inst) => {
      const av = inst.availability ?? []
      return av.some((a) => remainingDays.includes(a.day_of_week))
    })
  }

  if (f?.availableDate && !f?.availableToday && !f?.availableThisWeek) {
    const dow = new Date(f.availableDate + 'T12:00:00').getDay()
    results = await applyAvailabilityFilter(results, dow, f.availableDate)
  }

  // I1: filtro de disponibilidade por dia + período
  if (f?.availability) {
    const { dayOfWeek, period } = f.availability
    const [startH, endH] = PERIOD_HOURS[period]
    results = results.filter((inst) => {
      const av = inst.availability ?? []
      return av.some((a) => {
        if (a.day_of_week !== dayOfWeek) return false
        const slotStartH = Number(a.start_time.split(':')[0])
        const slotEndH   = Number(a.end_time.split(':')[0])
        // Verifica overlap com o período solicitado
        return slotStartH < endH && slotEndH > startH
      })
    })
  }

  return results
}

async function fetchInstructor(slug: string): Promise<InstructorWithExtras> {
  const { data, error } = await supabase
    .from('instructors')
    .select(
      `
      ${PUBLIC_INSTRUCTOR_FIELDS},
      profile:profiles!instructors_profile_id_fkey(id, full_name, avatar_url, role, city, state, is_pro_instructor, created_at),
      specialties:instructor_specialties(specialty),
      availability:availability(day_of_week, start_time, end_time),
      reviews:reviews(
        id,
        rating,
        comment,
        instructor_reply,
        instructor_reply_at,
        created_at,
        anonymous,
        student:profiles!reviews_student_id_fkey(
          full_name,
          avatar_url
        )
      )
      `,
    )
    .eq('slug', slug)
    .eq('status', 'approved')
    .order('created_at', { referencedTable: 'reviews', ascending: false })
    .limit(10, { referencedTable: 'reviews' })
    .single()

  // PGRST116 = slug sem instrutor: erro definitivo (a página mostra "não encontrado").
  if (error) throw Object.assign(new Error(error.message), { notFound: error.code === 'PGRST116' })
  return data as unknown as InstructorWithExtras
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useInstructors(filters?: InstructorFilters) {
  const query = useQuery<InstructorWithExtras[], Error>({
    queryKey: instructorKeys.list(filters),
    queryFn: () => fetchInstructors(filters),
    staleTime: 1000 * 60 * 3,
    retry: 0,
  })

  return {
    instructors: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}

function useInstructor(slug: string) {
  return useQuery<InstructorWithExtras, Error>({
    queryKey: instructorKeys.detail(slug),
    queryFn: () => fetchInstructor(slug),
    enabled: Boolean(slug),
    staleTime: 1000 * 60 * 5,
    retry: (count, err) => !(err as Error & { notFound?: boolean }).notFound && count < 3,
  })
}

export { useInstructors, useInstructor, instructorKeys }
export type { InstructorFilters, SortOption, InstructorWithExtras }
